import { ExternalLink } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Text } from '@/components/ui/Text'
import { cn } from '@/lib/utils'
import { knowledgeVideoEmbed, knowledgeVideoUrlAtTime, parseVideoTimestampLabel } from '../KnowledgeHubPage/videoUrl'

interface VideoPreviewProps {
  url: string
  title: string
  transcript?: string | null
  startAt?: string | null
  mediaKind?: 'video' | 'audio'
  hideOpenSource?: boolean
  className?: string
}

export function VideoPreview(props: VideoPreviewProps) {
  const { url, title, transcript, startAt, mediaKind, hideOpenSource, className } = props
  const { t } = useTranslation('app')
  const startSeconds = parseVideoTimestampLabel(startAt)
  const embed =
    knowledgeVideoEmbed(url, startSeconds) ||
    (url ? { kind: 'file' as const, src: url } : null)
  const sourceHref = knowledgeVideoUrlAtTime(url, startAt)
  const isAudio = mediaKind === 'audio'

  return (
    <div
      className={cn('flex min-h-0 w-full flex-1 flex-col gap-3', className)}
      data-testid="knowledge-video-preview"
    >
      {isAudio && embed?.kind === 'file' ? (
        <audio
          src={embed.src}
          controls
          className="w-full"
          data-testid="knowledge-audio-file"
        />
      ) : (
        <div className="relative w-full overflow-hidden rounded-xl bg-black pt-[56.25%]">
          {embed?.kind === 'iframe' ? (
            <iframe
              src={embed.src}
              title={title}
              className="absolute inset-0 h-full w-full border-0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              data-testid="knowledge-video-embed"
            />
          ) : embed?.kind === 'file' ? (
            <video
              src={embed.src}
              controls
              className="absolute inset-0 h-full w-full"
              data-testid="knowledge-video-file"
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center p-6">
              <Text size="sm" color="muted">
                {t('library.preview.cannotEmbed')}
              </Text>
            </div>
          )}
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <Text size="xs" color="muted">
          {t('library.preview.transcriptHint')}
        </Text>
        {hideOpenSource ? null : (
          <Button asChild variant="outline" size="sm" className="h-8 shrink-0">
            <a
              href={sourceHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="knowledge-video-open-source"
            >
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              {t('library.preview.openSource')}
            </a>
          </Button>
        )}
      </div>
      {transcript ? (
        <pre
          className="min-h-0 flex-1 overflow-y-auto whitespace-pre-wrap rounded-xl border border-border bg-background p-4 font-sans text-sm leading-relaxed"
          data-testid="knowledge-video-transcript"
        >
          {transcript}
        </pre>
      ) : null}
    </div>
  )
}
